import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AnimatedCounter } from '@/components/ui/animated-counter';

interface StatsSectionProps {
  aboutTitle: string;
  aboutDesc: string;
  stats: Array<{ value: string; label: string }>;
  aboutBtnUrl: string;
  aboutBtnText: string;
  aboutImgUrl: string;
  aboutQuote: string;
  aboutQuoteAuthor: string;
}

export const StatsSection: React.FC<StatsSectionProps> = ({
  aboutTitle,
  aboutDesc,
  stats,
  aboutBtnUrl,
  aboutBtnText,
  aboutImgUrl,
  aboutQuote,
  aboutQuoteAuthor,
}) => {
  const [isFactoryActive, setIsFactoryActive] = useState(false);

  return (
    <section className="py-16 lg:py-24 border-y border-white/5 relative">
      <div className="grid-cells-accent opacity-50" />
      <div className="container-custom relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-4 lg:space-y-8"
          >
            <h2
              className="font-extrabold uppercase tracking-tighter text-2xl lg:text-4xl"
              dangerouslySetInnerHTML={{ __html: aboutTitle }}
            />
            <p className="text-white/50 leading-relaxed text-xs lg:text-base max-w-[90vw] whitespace-pre-line">
              {aboutDesc}
            </p>
            
            <div className="grid grid-cols-2 pt-4 lg:pt-8 items-start gap-4 sm:gap-[var(--gap-stats)]">
              {stats.map((stat, idx) => (
                <div key={idx} className="flex flex-col gap-2 lg:gap-[clamp(0.5rem, 0.75vw, 0.75rem)]">
                  <div className="font-bold text-primary tracking-tighter text-[1.15rem] sm:text-[clamp(1.875rem, 2.5vw, 2.5rem)] leading-none min-h-[1.15rem] sm:min-h-[clamp(1.875rem,2.5vw,2.5rem)]">
                    {stat.value ? (
                      <AnimatedCounter value={stat.value} />
                    ) : (
                      <span className="invisible select-none" aria-hidden>—</span>
                    )}
                  </div>
                  <div className="uppercase tracking-wider text-white/60 leading-relaxed font-medium whitespace-pre-line text-[0.6rem] sm:text-[clamp(0.625rem, 0.6875vw, 0.6875rem)]">{stat.label}</div>
                </div>
              ))}
            </div>

            <Link to={aboutBtnUrl} className="inline-flex items-center gap-2 text-primary uppercase tracking-widest text-[10px] lg:text-xs font-bold hover:gap-4 hover:brightness-110 hover:underline underline-offset-4 decoration-primary/30 transition-all duration-300">
              {aboutBtnText} <ArrowRight size={16} />
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            onClick={() => setIsFactoryActive(!isFactoryActive)}
            className={cn(
              "relative aspect-video mirror-card group overflow-hidden transition-all duration-500",
              "cursor-pointer active:scale-[0.98]",
              isFactoryActive && "active-factory-state"
            )}
          >
            <style dangerouslySetInnerHTML={{ __html: `
              .active-factory-state {
                background-color: transparent !important;
                backdrop-filter: none !important;
                -webkit-backdrop-filter: none !important;
              }
              .active-factory-state img {
                filter: none !important;
                -webkit-filter: none !important;
                opacity: 1 !important;
                mix-blend-mode: normal !important;
              }
              .active-factory-state::before,
              .active-factory-state::after {
                display: none !important;
                opacity: 0 !important;
                background: transparent !important;
              }

              @media (min-width: 1025px) {
                .group:hover .factory-img {
                  filter: none !important;
                  opacity: 1 !important;
                  transform: scale(1.05) !important;
                }
                .group:hover .factory-overlay {
                  opacity: 0 !important;
                }
                .group:hover::before,
                .group:hover::after {
                  opacity: 0 !important;
                  background: transparent !important;
                }
              }
            ` }} />
            <img
              src={aboutImgUrl}
              alt="SARGO Full Cycle Plant"
              loading="lazy"
              className={cn(
                "factory-img w-full h-full object-cover grayscale opacity-80",
                "transition-all ease-in-out",
                isFactoryActive && "grayscale-0 opacity-100 scale-105"
              )}
              style={{
                transition: 'transform 600ms ease, filter 250ms ease, opacity 250ms ease'
              }}
            />
            <div className={cn(
              "factory-overlay absolute inset-0 bg-black/40 pointer-events-none transition-opacity duration-[250ms]",
              isFactoryActive ? 'opacity-0' : 'opacity-100'
            )}></div>
            
            <div className={cn(
              "factory-overlay absolute inset-0 bg-primary/10 mix-blend-overlay transition-opacity duration-[250ms] pointer-events-none",
              "hidden lg:block",
              isFactoryActive ? "opacity-0" : "opacity-100"
            )}></div>

            <div className="absolute bottom-3 right-3 lg:bottom-8 lg:right-8 bg-background/90 backdrop-blur-md p-2.5 sm:p-4 lg:p-6 border border-white/5 max-w-[90%] md:max-w-[75%] lg:max-w-xs h-auto transition-all duration-300">
              <p className="italic text-[10px] lg:text-sm text-white/70 whitespace-normal break-words [overflow-wrap:anywhere] leading-relaxed">{aboutQuote}</p>
              <div className="mt-3 lg:mt-4 flex items-start gap-2 lg:gap-3">
                <div className="w-4 lg:w-8 h-[1px] bg-primary mt-2"></div>
                <div className="flex flex-col">
                  <span className="text-[7px] sm:text-[8px] lg:text-[10px] uppercase tracking-widest font-bold leading-tight">{aboutQuoteAuthor}</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
